import { DependencyList, useEffect, useState } from 'react';
import {
  getSessionStoreString,
  removeSessionStoreItem,
  setSessionStoreString,
} from 'services/storageUtils';
import { compactHash, stableStringify } from 'services/stringUtils';

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

function depsToKey(deps: DependencyList): string {
  if (Array.isArray(deps)) {
    return compactHash(JSON.stringify(deps));
  }
  const stableJson = stableStringify(deps);
  return compactHash(stableJson);
}

type CacheEntry<T> = {
  value: T;
  depsKey: string; // key for dependencies
  expiresAt: number;
};

function getSessionCache<T>(key: string): CacheEntry<T> | undefined {
  try {
    const raw = getSessionStoreString(key);
    if (!raw) return undefined;
    return JSON.parse(raw) as CacheEntry<T>;
  } catch {
    return undefined;
  }
}

function setSessionCache<T>(key: string, entry: CacheEntry<T>) {
  try {
    setSessionStoreString(key, JSON.stringify(entry));
  } catch {
    // Ignore storage errors
  }
}

function removeSessionCache(key: string) {
  try {
    removeSessionStoreItem(key);
  } catch {
    // Ignore storage errors
  }
}

export function useAsyncMemo<T>(
  factory: () => Promise<T> | undefined | null,
  deps: DependencyList,
  initial?: T,
  cacheKey?: string,
  enableCache: boolean = true,
  ttlMs: number = DEFAULT_TTL_MS,
): [T | undefined, boolean] {
  const [val, setVal] = useState<T | undefined>(initial);
  const [loading, setLoading] = useState<boolean>(false);

  const key = cacheKey ?? `useAsyncMemo:${depsToKey(deps)}`;
  const depsKey = depsToKey(deps);

  useEffect(() => {
    const now = Date.now();

    if (enableCache) {
      const entry = getSessionCache<T>(key);
      if (entry && entry.expiresAt > now && entry.depsKey === depsKey) {
        setVal(entry.value);
        setLoading(false);
        return;
      } else if (entry) {
        removeSessionCache(key);
      }
    }

    let cancel = false;
    setLoading(true);
    const promise = factory();
    if (promise === undefined || promise === null) {
      setLoading(false);
      return;
    }

    promise
      .then((result) => {
        if (!cancel) {
          setVal(result);
          setLoading(false);
          if (enableCache) {
            setSessionCache<T>(key, {
              value: result,
              depsKey: depsKey,
              expiresAt: Date.now() + ttlMs,
            });
          }
        }
      })
      .catch(() => {
        if (!cancel) setLoading(false);
      });

    return () => {
      cancel = true;
    };
  }, [depsKey, key, enableCache, ttlMs]);

  return [val, loading];
}
